import type { AuthorizationRuleForGating } from "../use-gating-permissions";
import type {
  GatingMaskInformation,
  GatingRuleMetadata,
} from "../gating-types";

/**
 * Strategy types supported by the gating system.
 */
export enum GatingStrategyType {
  membership = "membership",
  token = "token",
  points = "points",
  product = "product",
}

/**
 * Strategy interface for gating evaluation.
 * Each strategy handles a specific type of gating criterion.
 *
 * IMPORTANT: Strategies are internal implementation details.
 * Consumer code should use useGatingEvaluator() instead.
 */
export interface GatingStrategy {
  /**
   * Unique identifier for this strategy type.
   */
  readonly strategyType: GatingStrategyType;

  /**
   * Check if this strategy can evaluate the given rule.
   * Returns true if the rule has the criterion type this strategy handles.
   */
  canEvaluate(rule: AuthorizationRuleForGating): boolean;

  /**
   * Evaluate whether the user meets the rule's criterion.
   * Only called if canEvaluate() returns true.
   */
  matchRule(rule: AuthorizationRuleForGating): boolean;

  /**
   * Extract gating mask information from a rule.
   */
  getGatingMaskInformation(
    rule: AuthorizationRuleForGating | null,
  ): GatingMaskInformation | null;

  /**
   * Extract gating metadata from a rule.
   */
  getGatingRuleMetadata(
    rule: AuthorizationRuleForGating | null,
  ): GatingRuleMetadata | null;
}
